import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { SharedLayout } from '../components/layout/SharedLayout';
import { Container } from '../components/layout/Container';
import { Button } from '../components/ui/Button';
import { useCalendlyPopup } from '../hooks/useCalendlyPopup';

const ContactWrapper = styled.div`
  padding: calc(80px + ${theme.spacing.xl}) ${theme.spacing.md} ${theme.spacing.xl} ${theme.spacing.md};
  min-height: 80vh;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    padding: calc(80px + ${theme.spacing['3xl']}) 0 ${theme.spacing['3xl']} 0;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing['2xl']};
  max-width: 1200px;
  margin: 0 auto;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
    gap: ${theme.spacing['3xl']};
  }
`;

const Section = styled.div`
  padding: 0;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.xl};
  }
`;

const Title = styled.h1`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.primary.dark};
  margin-bottom: ${theme.spacing.xl};
  text-align: center;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes['3xl']};
    margin-bottom: ${theme.spacing['2xl']};
    text-align: left;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  background: linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%);
  border: 2px solid ${theme.colors.primary.blue}20;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md};
  box-shadow: 0 4px 20px rgba(77, 101, 255, 0.08);
  width: 100%;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    gap: ${theme.spacing.lg};
    padding: ${theme.spacing['2xl']};
  }
`;

const FormHeader = styled.h2`
  font-size: ${theme.typography.sizes.md};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.primary.dark};
  margin-bottom: ${theme.spacing.xs};
  text-align: center;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes.xl};
    margin-bottom: ${theme.spacing.md};
  }
`;

const FormSubheader = styled.p`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.neutral.darkGrey};
  margin-bottom: ${theme.spacing.md};
  text-align: center;
  line-height: 1.6;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes.md};
    margin-bottom: ${theme.spacing.xl};
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  width: 100%;
`;

const Label = styled.label`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.fontWeights.medium};
  color: ${theme.colors.neutral.darkGrey};
`;

const Input = styled.input`
  padding: ${theme.spacing.sm} ${theme.spacing.sm};
  border: 2px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: 16px; /* Prevents zoom on iOS */
  transition: border-color ${theme.transitions.base};
  width: 100%;
  min-height: 44px; /* Touch target size */
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.blue};
  }
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.md};
    font-size: ${theme.typography.sizes.md};
  }
`;

const TextArea = styled.textarea`
  padding: ${theme.spacing.sm} ${theme.spacing.sm};
  border: 2px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: 16px; /* Prevents zoom on iOS */
  min-height: 100px;
  resize: vertical;
  transition: border-color ${theme.transitions.base};
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.blue};
  }
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.md};
    font-size: ${theme.typography.sizes.md};
    min-height: 150px;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing['2xl']};
`;

const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const InfoTitle = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.fontWeights.semibold};
  color: ${theme.colors.primary.dark};
`;

const InfoText = styled.p`
  color: ${theme.colors.neutral.darkGrey};
  line-height: 1.6;
`;

const InfoLink = styled.a`
  color: ${theme.colors.primary.blue};
  text-decoration: none;
  font-weight: ${theme.typography.fontWeights.medium};
  
  &:hover {
    text-decoration: underline;
  }
`;

const SuccessMessage = styled.div`
  padding: ${theme.spacing.lg};
  background: #10b98120;
  border: 1px solid #10b981;
  border-radius: ${theme.borderRadius.md};
  color: #10b981;
  font-weight: ${theme.typography.fontWeights.medium};
`;

const ContactOptionsSection = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing['2xl']};
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
    gap: ${theme.spacing.xl};
    margin-bottom: ${theme.spacing['3xl']};
  }
`;

const ContactCard = styled.div`
  background: white;
  border: 2px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  text-align: center;
  transition: all ${theme.transitions.base};
  
  &:hover {
    border-color: ${theme.colors.primary.blue};
    box-shadow: ${theme.shadows.lg};
  }
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing['2xl']};
  }
`;

const ContactIcon = styled.div`
  font-size: 36px;
  margin-bottom: ${theme.spacing.md};
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    font-size: 48px;
    margin-bottom: ${theme.spacing.lg};
  }
`;

const ContactCardTitle = styled.h2`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.primary.dark};
  margin-bottom: ${theme.spacing.sm};
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes.xl};
    margin-bottom: ${theme.spacing.md};
  }
`;

const ContactCardDescription = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.neutral.darkGrey};
  margin-bottom: ${theme.spacing.md};
  line-height: 1.6;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes.md};
    margin-bottom: ${theme.spacing.lg};
  }
`;

const PhoneNumber = styled.a`
  display: block;
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.primary.blue};
  text-decoration: none;
  margin-bottom: ${theme.spacing.sm};
  
  &:hover {
    text-decoration: underline;
  }
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes['2xl']};
  }
`;

const ContactHours = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.neutral.grey};
  margin-bottom: ${theme.spacing.md};
`;

const ContactHighlight = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.primary.dark};
  font-style: italic;
  margin-top: ${theme.spacing.md};
`;

const OptionsHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['2xl']};
  background: linear-gradient(135deg, #e8f4fd 0%, #d4e6ff 100%);
  padding: ${theme.spacing['2xl']} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.xl};
  border: 1px solid ${theme.colors.primary.blue}15;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    margin-bottom: ${theme.spacing['3xl']};
    padding: ${theme.spacing['3xl']} ${theme.spacing['2xl']};
  }
`;

const OptionsTitle = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.primary.dark};
  margin-bottom: ${theme.spacing.md};
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes['2xl']};
    margin-bottom: ${theme.spacing.lg};
  }
`;

const OptionsSubtitle = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.neutral.darkGrey};
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes.lg};
    line-height: 1.7;
  }
`;

const ScheduleButton = styled(Button)`
  background: linear-gradient(135deg, ${theme.colors.primary.blue} 0%, #5d75ff 100%);
  color: white;
  font-weight: ${theme.typography.fontWeights.bold};
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border: none;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  margin-top: ${theme.spacing.md};
  box-shadow: 0 4px 15px rgba(77, 101, 255, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(77, 101, 255, 0.4);
  }
`;

const ProductInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  margin-bottom: ${theme.spacing.sm};
  width: 100%;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    align-items: center;
    margin-bottom: 0;
  }
`;

const AddProductButton = styled.button`
  background: ${theme.colors.primary.blue};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.fontWeights.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  min-height: 44px;
  width: 100%;
  margin-top: ${theme.spacing.xs};
  
  &:hover {
    background: ${theme.colors.primary.dark};
  }
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.md} ${theme.spacing.lg};
    width: auto;
    margin-top: 0;
  }
`;

const RemoveProductButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: ${theme.typography.sizes.sm};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  min-height: 44px;
  width: 100%;
  
  &:hover {
    background: #dc2626;
  }
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    width: auto;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.sm};
  background: #f8f9fa;
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing.md};
  max-height: 400px;
  overflow-y: auto;
  width: 100%;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.lg};
    gap: ${theme.spacing.sm};
    max-height: none;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.neutral.darkGrey};
  cursor: pointer;
  padding: ${theme.spacing.sm} 0;
  min-height: 32px;
  line-height: 1.4;
  word-break: break-word;
  
  input {
    margin-top: 2px;
    min-width: 20px;
    min-height: 20px;
    flex-shrink: 0;
  }
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes.sm};
    padding: ${theme.spacing.xs} 0;
    
    input {
      min-width: 18px;
      min-height: 18px;
    }
  }
`;

const FileUploadArea = styled.div`
  border: 2px dashed ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  text-align: center;
  background: #f8f9fa;
  margin-bottom: ${theme.spacing.md};
  width: 100%;
  
  input[type="file"] {
    display: none;
  }
  
  label {
    display: inline-block;
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    background: ${theme.colors.primary.blue};
    color: white;
    border-radius: ${theme.borderRadius.md};
    cursor: pointer;
    font-weight: ${theme.typography.fontWeights.medium};
    font-size: ${theme.typography.sizes.sm};
    transition: all ${theme.transitions.base};
    min-height: 44px;
    width: 100%;
    max-width: none;
    
    &:hover {
      background: ${theme.colors.primary.dark};
    }
  }
  
  p {
    margin-top: ${theme.spacing.sm};
    font-size: ${theme.typography.sizes.xs};
    color: ${theme.colors.neutral.grey};
  }
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.xl};
    margin-bottom: ${theme.spacing.lg};
    
    label {
      padding: ${theme.spacing.md} ${theme.spacing.xl};
      width: auto;
      max-width: 250px;
    }
    
    p {
      font-size: ${theme.typography.sizes.sm};
      margin-top: ${theme.spacing.md};
    }
  }
`;

const DeepDiveSection = styled.div`
  margin-top: ${theme.spacing.xl};
  padding: ${theme.spacing.md};
  background: linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%);
  border-radius: ${theme.borderRadius.xl};
  border: 2px solid #ffd700;
  width: 100%;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    margin-top: ${theme.spacing['3xl']};
    padding: ${theme.spacing['3xl']};
  }
`;

const DeepDiveTitle = styled.h2`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.fontWeights.bold};
  color: ${theme.colors.primary.dark};
  margin-bottom: ${theme.spacing.sm};
  text-align: center;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes['2xl']};
    margin-bottom: ${theme.spacing.lg};
  }
`;

const DeepDiveSubtitle = styled.p`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.neutral.darkGrey};
  text-align: center;
  margin-bottom: ${theme.spacing.md};
  line-height: 1.6;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes.lg};
    margin-bottom: ${theme.spacing['2xl']};
  }
`;

export const Contact: React.FC = () => {
  const { openCalendly } = useCalendlyPopup();
  
  useEffect(() => {
    // Check if there's a hash in the URL and scroll to it
    if (window.location.hash === '#deep-dive-section') {
      setTimeout(() => {
        const element = document.getElementById('deep-dive-section');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, []);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [products, setProducts] = useState([{ company: '', product: '' }]);
  const [selectedFAQs, setSelectedFAQs] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [submitted, setSubmitted] = useState(false);

  // Anti-spam fields
  const [honeypot, setHoneypot] = useState('');
  const [formLoadTime] = useState(Date.now());
  
  const faqs = [
    "How does this compare to a simple S&P 500 index annuity?",
    "What are the caps and participation rates?",
    "How do managed volatility indices affect my returns?",
    "What is the date lottery effect?",
    "Are the illustrations based on real or hypothetical data?",
    "What are the surrender charges and fees?",
    "How has this product performed in real client accounts?",
    "What happens if I need to access my money early?",
    "How is this product taxed?",
    "What death benefits are available?"
  ];
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Anti-spam validation
    if (honeypot) {
      // Bot detected - honeypot field was filled
      console.warn('Spam detected: honeypot field filled');
      return;
    }

    const timeSpent = Date.now() - formLoadTime;
    if (timeSpent < 3000) {
      // Submitted too quickly (less than 3 seconds)
      console.warn('Spam detected: form submitted too quickly');
      return;
    }

    // Handle form submission here
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleProductChange = (index: number, field: 'company' | 'product', value: string) => {
    const newProducts = [...products];
    newProducts[index][field] = value;
    setProducts(newProducts);
  };
  
  const addProduct = () => {
    setProducts([...products, { company: '', product: '' }]);
  };
  
  const removeProduct = (index: number) => {
    const newProducts = products.filter((_, i) => i !== index);
    setProducts(newProducts.length > 0 ? newProducts : [{ company: '', product: '' }]);
  };
  
  const handleFAQToggle = (faq: string) => {
    setSelectedFAQs(prev => 
      prev.includes(faq) 
        ? prev.filter(f => f !== faq)
        : [...prev, faq]
    );
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments(Array.from(e.target.files));
    }
  };
  
  const handleDeepDiveSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Anti-spam validation
    if (honeypot) {
      // Bot detected - honeypot field was filled
      console.warn('Spam detected: honeypot field filled');
      return;
    }

    const timeSpent = Date.now() - formLoadTime;
    if (timeSpent < 3000) {
      // Submitted too quickly (less than 3 seconds)
      console.warn('Spam detected: form submitted too quickly');
      return;
    }

    // Handle deep dive form submission
    console.log('Deep Dive submitted:', {
      formData,
      products,
      selectedFAQs,
      attachments
    });
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };
  
  return (
    <SharedLayout>
      <Container>
        <ContactWrapper>
          <Title>Get in Touch</Title>
          
          <OptionsHeader>
            <OptionsTitle>We're Here to Help With Your Annuity Questions</OptionsTitle>
            <OptionsSubtitle>
              No pressure, just answers. Whether you prefer to call us directly, schedule a consultation at your convenience, or simply send us your questions - we're here to help you understand your options and make informed decisions.
            </OptionsSubtitle>
          </OptionsHeader>
          
          {/* Main Contact Section - Senior Optimized */}
          <div style={{
            background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f3ff 100%)',
            borderRadius: '20px',
            padding: window.innerWidth < 768 ? '20px' : '40px',
            marginBottom: '40px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)'
          }}>
            {/* Primary Call Now Section */}
            <div style={{
              background: '#ffd700',
              padding: '30px',
              borderRadius: '16px',
              marginBottom: '35px',
              textAlign: 'center',
              boxShadow: '0 5px 15px rgba(255, 215, 0, 0.3)'
            }}>
              <p style={{
                fontSize: '20px',
                color: '#232B3B',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px'
              }}>
                <span style={{ fontSize: '26px' }}>📞</span>
                Call Now - Cap Averill II & Associates
              </p>
              <a href="tel:800-922-1894" style={{
                color: '#232B3B',
                textDecoration: 'none',
                fontSize: '42px',
                fontWeight: '900',
                display: 'block',
                background: 'rgba(255, 255, 255, 0.8)',
                padding: '20px',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                letterSpacing: '2px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
                e.currentTarget.style.transform = 'scale(1.03)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                e.currentTarget.style.transform = 'scale(1)';
              }}>
                800-922-1894
              </a>
              <p style={{
                fontSize: '16px',
                color: '#666',
                marginTop: '12px',
                fontStyle: 'italic'
              }}>
                Tap to call now - We're here to help!
              </p>
            </div>

            {/* Cap's Direct Line Section */}
            <div style={{
              background: 'white',
              border: '3px solid #10a37f',
              borderRadius: '16px',
              padding: '35px',
              marginBottom: '35px'
            }}>
              <h3 style={{
                fontSize: '26px',
                color: '#232B3B',
                textAlign: 'center',
                marginBottom: '25px',
                fontWeight: '700',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '30px' }}>🔥</span>
                Cap's Direct Line
              </h3>
              
              <a href="tel:419-309-7797" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '15px',
                background: 'linear-gradient(135deg, #10a37f 0%, #13c89f 100%)',
                padding: '22px',
                borderRadius: '12px',
                textDecoration: 'none',
                marginBottom: '20px',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(16, 163, 127, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.02)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 163, 127, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 163, 127, 0.3)';
              }}>
                <span style={{ fontSize: '30px' }}>📞</span>
                <span style={{
                  color: 'white',
                  fontSize: '32px',
                  fontWeight: '800',
                  letterSpacing: '1px'
                }}>419-309-7797</span>
                <span style={{
                  background: 'white',
                  color: '#10a37f',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '700'
                }}>TEXT OK</span>
              </a>
              
              <div style={{
                background: '#fff9e6',
                border: '2px solid #ffd700',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <p style={{
                  fontSize: '18px',
                  color: '#232B3B',
                  textAlign: 'center',
                  margin: '0',
                  lineHeight: '1.6'
                }}>
                  <strong style={{ color: '#d32f2f' }}>⚡ High Demand Notice:</strong><br/>
                  Cap is often with other clients. Leave a message or text - he WILL get back to you!<br/>
                  <span style={{ fontWeight: '700', color: '#10a37f', fontSize: '20px' }}>Current wait: 7-10 days</span>
                </p>
              </div>
              
              <a href="mailto:cap@capassociates.net" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                background: '#f8f9fa',
                color: '#232B3B',
                textDecoration: 'none',
                fontSize: '20px',
                fontWeight: '600',
                padding: '18px',
                borderRadius: '10px',
                border: '2px solid #e0e0e0',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e8f4fd';
                e.currentTarget.style.borderColor = '#4d65ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f8f9fa';
                e.currentTarget.style.borderColor = '#e0e0e0';
              }}>
                <span style={{ fontSize: '24px' }}>✉️</span>
                cap@capassociates.net
              </a>
            </div>

            {/* Schedule Appointment Button */}
            <button
              onClick={openCalendly}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                color: '#232B3B',
                padding: '25px',
                border: 'none',
                borderRadius: '12px',
                fontSize: '22px',
                fontWeight: '800',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                transition: 'all 0.3s ease',
                boxShadow: '0 6px 20px rgba(255, 215, 0, 0.3)',
                marginBottom: '35px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(255, 215, 0, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 215, 0, 0.3)';
              }}>
              📅 Schedule With Cap
            </button>

            {/* Need Answers Today Section */}
            <div style={{
              background: 'white',
              border: '2px solid #e0e0e0',
              borderRadius: '12px',
              padding: '25px',
              textAlign: 'center',
              marginBottom: '35px'
            }}>
              <p style={{
                fontSize: '20px',
                color: '#232B3B',
                marginBottom: '18px',
                fontWeight: '600'
              }}>
                Need answers today? Our team is ready:
              </p>
              <a href="tel:800-922-1894" style={{
                background: 'linear-gradient(135deg, #10a37f 0%, #13c89f 100%)',
                color: 'white',
                textDecoration: 'none',
                fontSize: '24px',
                fontWeight: '700',
                padding: '18px 35px',
                borderRadius: '10px',
                display: 'inline-block',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(16, 163, 127, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 163, 127, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(16, 163, 127, 0.3)';
              }}>
                📞 800-922-1894
              </a>
            </div>

            {/* Quick Access Tools */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr',
              gap: '20px'
            }}>
              <button
                onClick={() => window.location.href = '/#quick-answer'}
                style={{
                  background: 'white',
                  color: '#10a37f',
                  padding: '20px',
                  border: '2px solid #10a37f',
                  borderRadius: '10px',
                  fontSize: '18px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(16, 163, 127, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                ⚡ Quick Answer
              </button>
              
              <button
                onClick={() => window.location.href = '/#deep-dive'}
                style={{
                  background: '#232B3B',
                  color: '#ffd700',
                  padding: '20px',
                  border: '2px solid #232B3B',
                  borderRadius: '10px',
                  fontSize: '18px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#1a1f2e';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#232B3B';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                🔍 Deep Dive
              </button>
            </div>
          </div>
          
          <ContactGrid>
            <Section>
              {submitted ? (
                <SuccessMessage>
                  Thank you for your message. We'll get back to you within 24 hours.
                </SuccessMessage>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <FormHeader>Quick Answer Form</FormHeader>
                  <FormSubheader>
                    Get a quick response about your specific annuity product. One of our delightful staff will contact you ASAP!
                  </FormSubheader>
                  <FormGroup>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="phone">Phone (Optional)</Label>
                    <Input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <Label htmlFor="message">Message</Label>
                    <TextArea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>

                  {/* Honeypot field - hidden from users, catches bots */}
                  <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                    <label htmlFor="website">Website</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <Button type="submit" variant="primary">
                    Get Quick Answer
                  </Button>
                </Form>
              )}
            </Section>
            
            <Section>
              <ContactInfo>
                <InfoBlock>
                  <InfoTitle>Phone</InfoTitle>
                  <InfoText>
                    <InfoLink href="tel:800-922-1894">800-922-1894</InfoLink>
                  </InfoText>
                  <InfoText>
                    Monday: 9AM-5PM<br />
                    Tuesday: 9AM-5PM<br />
                    Wednesday: 9AM-5PM<br />
                    Thursday: 9AM-5PM<br />
                    Friday: 9AM-5PM<br />
                    Saturday: Closed<br />
                    Sunday: Closed
                  </InfoText>
                </InfoBlock>
                
                {/* <InfoBlock>
                  <InfoTitle>Email</InfoTitle>
                  <InfoText>
                    <InfoLink href="mailto:info@annuityanswers.com">
                      info@annuityanswers.com
                    </InfoLink>
                  </InfoText>
                </InfoBlock> */}
                
                <InfoBlock>
                  <InfoTitle>Address</InfoTitle>
                  <InfoText>
                    Annuity Answers<br />
                    2600 N Reynolds Rd #104a<br />
                    Toledo, OH 43615
                  </InfoText>
                </InfoBlock>
                
                <InfoBlock>
                  <InfoTitle>Response Time</InfoTitle>
                  <InfoText>
                    We typically respond within 24 hours during business days.
                  </InfoText>
                </InfoBlock>
              </ContactInfo>
            </Section>
          </ContactGrid>
          
          <DeepDiveSection id="deep-dive-section">
            <DeepDiveTitle>Request Your Deep Dive Analysis</DeepDiveTitle>
            <DeepDiveSubtitle>
              Perfect for agents or anyone who needs to analyze multiple products. 
              Get comprehensive analysis with real data, not hypothetical illustrations.
            </DeepDiveSubtitle>
            
            {submitted ? (
              <SuccessMessage>
                Thank you for requesting a Deep Dive! Our team will analyze your products and contact you within 24-48 hours with comprehensive insights.
              </SuccessMessage>
            ) : (
              <Form onSubmit={handleDeepDiveSubmit}>
                <FormHeader>Deep Dive Request Form</FormHeader>
                
                <FormGroup>
                  <Label>Products to Analyze</Label>
                  {products.map((product, index) => (
                    <ProductInput key={index}>
                      <Input
                        type="text"
                        placeholder="Company Name"
                        value={product.company}
                        onChange={(e) => handleProductChange(index, 'company', e.target.value)}
                        required
                      />
                      <Input
                        type="text"
                        placeholder="Product Name"
                        value={product.product}
                        onChange={(e) => handleProductChange(index, 'product', e.target.value)}
                        required
                      />
                      {products.length > 1 && (
                        <RemoveProductButton type="button" onClick={() => removeProduct(index)}>
                          Remove
                        </RemoveProductButton>
                      )}
                    </ProductInput>
                  ))}
                  <AddProductButton type="button" onClick={addProduct}>
                    + Add Another Product
                  </AddProductButton>
                </FormGroup>
                
                <FormGroup>
                  <Label>Contact Information</Label>
                  <Input
                    type="text"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    name="name"
                    required
                  />
                  <Input
                    type="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleChange}
                    name="email"
                    required
                    style={{ marginTop: theme.spacing.sm }}
                  />
                  <Input
                    type="tel"
                    placeholder="Phone Number (Optional)"
                    value={formData.phone}
                    onChange={handleChange}
                    name="phone"
                    style={{ marginTop: theme.spacing.sm }}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label style={{ marginBottom: '15px', fontSize: '18px', fontWeight: '600' }}>Optional Information (Provide as much as you can)</Label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                    <Input
                      type="text"
                      placeholder="Current Carrier"
                      name="currentCarrier"
                    />
                    <Input
                      type="text"
                      placeholder="Current Product"
                      name="currentProduct"
                    />
                    <Input
                      type="text"
                      placeholder="Single Premium or Flexible"
                      name="premiumType"
                    />
                    <Input
                      type="date"
                      placeholder="Contract Inception Date"
                      name="inceptionDate"
                    />
                    <Input
                      type="text"
                      placeholder="Total Deposited into Annuity"
                      name="totalDeposited"
                    />
                    <Input
                      type="text"
                      placeholder="Current Accumulated Value"
                      name="accumulatedValue"
                    />
                    <Input
                      type="text"
                      placeholder="Current Surrender Value"
                      name="surrenderValue"
                    />
                    <Input
                      type="text"
                      placeholder="Available Penalty Free Withdrawal (%)"
                      name="penaltyFreePercent"
                    />
                    <Input
                      type="text"
                      placeholder="Available Penalty Free Withdrawal ($)"
                      name="penaltyFreeAmount"
                    />
                    <Input
                      type="text"
                      placeholder="Systematic Withdrawals/RMDs (Monthly/Quarterly/Annually)"
                      name="withdrawalFrequency"
                    />
                    <Input
                      type="text"
                      placeholder="Withdrawal Amount"
                      name="withdrawalAmount"
                    />
                    <Input
                      type="text"
                      placeholder="Approx Actual Growth Per Year Since Inception (%)"
                      name="actualGrowth"
                    />
                  </div>
                </FormGroup>
                
                <FormGroup>
                  <Label>What are your concerns about this contract?</Label>
                  <TextArea
                    placeholder="Please describe your concerns, questions, or what you'd like to know about this annuity contract..."
                    value={formData.message}
                    onChange={handleChange}
                    name="message"
                    style={{ minHeight: '150px' }}
                  />
                </FormGroup>
                
                <FileUploadArea>
                  <label htmlFor="file-upload">
                    Attach Statements (Optional)
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={handleFileChange}
                  />
                  <p>Upload statements or illustrations (PDF, JPG, PNG, DOC)</p>
                  {attachments.length > 0 && (
                    <p style={{ color: theme.colors.primary.blue, marginTop: theme.spacing.md }}>
                      {attachments.length} file(s) selected
                    </p>
                  )}
                </FileUploadArea>

                {/* Honeypot field - hidden from users, catches bots */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                  <label htmlFor="website-deep">Website</label>
                  <input
                    type="text"
                    id="website-deep"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <Button type="submit" variant="primary" style={{ width: '100%' }}>
                  Submit Deep Dive Request
                </Button>
              </Form>
            )}
          </DeepDiveSection>
        </ContactWrapper>
      </Container>
    </SharedLayout>
  );
};